import { autoinject, bindingMode, observable } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { AuthService } from 'app/um/services/auth-service';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/essette-api';
import { deepClone, forceNull } from 'app/shared/lib/utilities';
import { getReference } from 'app/shared/models/essette-types';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class SaveAuthQueue extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ defaultBindingMode: bindingMode.toView })
    protected queue: UM.AuthQueue | null = null;

    protected selectedStatuses: Array<UM.AuthStatus> = [];

    protected queueStatuses: ReadonlyArray<UM.AuthStatus> = [];

    @observable
    protected allowAnyStatus: boolean = false;

    protected maxDescriptionLength: number = 30;
    protected maxCodeLength: number = 50;

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AuthService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected attached(): void {
        // load lookups, do not wait for results
        this.reload();
    }

    protected queueChanged() {
        this.selectedStatuses = this.queue?.statuses ?? [];
        this.allowAnyStatus = this.queue?.flag?.allowAnyStatus || false;
    }

    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        await this.loadAuthStatuses();
    }

    protected allowAnyStatusChanged(): void {
        this.selectedStatuses = this.allowAnyStatus ? [] : this.queue?.statuses || [];
    }

    ////****************** Functions ************************************************////
    protected constructSaveObject(): UM.AuthQueue {
        if(!this.queue) {
            throw new Error('Bindable property "queue" must be bound');
        }

        const saveQueue: UM.AuthQueue = {
            id: this.queue.id,
            description: this.queue.description,
            code: this.queue.code,
            flag: {
                allowAnyStatus: this.allowAnyStatus
            },
            statuses: []
        }

        this.selectedStatuses.forEach(status => {
            const statusReference = getReference(status);
            if(statusReference) {
                saveQueue.statuses?.push(statusReference);
            }
        });

        return saveQueue;
    }

    public reset(): void {
        this.selectedStatuses = [];
        this.queue = null;
        this.allowAnyStatus = false;
    }

    public async submit(): Promise<boolean> {
        return this.saveQueue();
    }

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadAuthStatuses(): Promise<void> {
        const includes: string[] = [];

        const options: Partial<IQueryLoadOptions> = {
            all: true,
            includeInactive: false
        };

        try {
            this.state.beginLoad();

            const response = await this.service.loadAuthStatuses(options, includes);
            this.queueStatuses = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveQueue(): Promise<boolean> {
        if(!this.queue) return false;

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.saveQueue(this.constructSaveObject());
            isSuccessful = response.isSuccessful;
            return isSuccessful;
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.saved('um:queue.$long', isSuccessful);
            this.state.endSave();
        }
    }
}
